/**
 * src/toast/ToastProvider.jsx
 *
 * App-wide toast notifications. Mount <ToastProvider> once near the app root;
 * call `const toast = useToast()` anywhere below it and fire:
 *
 *   toast.error("We couldn't save your changes.", { correlationId: err.correlationId });
 *   toast.success("Booking link created.");
 *   toast.info("Copied to clipboard.");
 *
 * Toasts auto-dismiss (errors linger longer than successes) and can be closed
 * manually. When a `correlationId` is supplied it's shown as a small monospace
 * "Reference: …" line so a user can quote it to support — it maps to the server
 * log's invocationId. Form-field validation stays inline in the forms; toasts
 * are for async / load / action failures.
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useReducer, useRef } from "react";

const ToastContext = createContext(null);

// Errors stay longer (need reading); successes are quick acknowledgements.
const DEFAULT_DURATIONS = { error: 8000, success: 4000, info: 5000 };

function reducer(state, action) {
  switch (action.type) {
    case "add":     return [...state, action.toast];
    case "dismiss": return state.filter((t) => t.id !== action.id);
    default:        return state;
  }
}

export function ToastProvider({ children }) {
  const [toasts, dispatch] = useReducer(reducer, []);
  const idRef  = useRef(0);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    dispatch({ type: "dismiss", id });
    const handle = timers.current.get(id);
    if (handle) { clearTimeout(handle); timers.current.delete(id); }
  }, []);

  const push = useCallback((variant, message, opts = {}) => {
    if (!message) return undefined;
    const id = ++idRef.current;
    const duration = opts.duration ?? DEFAULT_DURATIONS[variant] ?? 5000;
    dispatch({ type: "add", toast: { id, variant, message, correlationId: opts.correlationId ?? null } });
    if (duration > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration));
    }
    return id;
  }, [dismiss]);

  const toast = useMemo(() => ({
    error:   (message, opts) => push("error",   message, opts),
    success: (message, opts) => push("success", message, opts),
    info:    (message, opts) => push("info",    message, opts),
    dismiss,
  }), [push, dismiss]);

  // Clear any pending auto-dismiss timers when the provider unmounts.
  useEffect(() => {
    const timerMap = timers.current;
    return () => { for (const handle of timerMap.values()) clearTimeout(handle); };
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

// ── Presentation ────────────────────────────────────────────────────────────

const VARIANT_STYLES = {
  error:   "bg-red-50 border-red-300 text-red-800",
  success: "bg-emerald-50 border-emerald-300 text-emerald-800",
  info:    "bg-slate-50 border-slate-300 text-slate-800",
};

const VARIANT_ICON = { error: "⚠", success: "✓", info: "ℹ" };

function Toaster({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const style = VARIANT_STYLES[toast.variant] || VARIANT_STYLES.info;
  return (
    <div
      className={`pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2 shadow-md ${style}`}
      role={toast.variant === "error" ? "alert" : "status"}
      aria-live={toast.variant === "error" ? "assertive" : "polite"}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 font-semibold">
        {VARIANT_ICON[toast.variant] || VARIANT_ICON.info}
      </span>
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm">{toast.message}</p>
        {toast.correlationId && (
          <p className="mt-1 font-mono text-[11px] opacity-70">Reference: {toast.correlationId}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 rounded p-0.5 text-lg leading-none opacity-60 hover:opacity-100"
      >
        ×
      </button>
    </div>
  );
}
